import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Form, Button, Alert, Container, Row, Col } from "react-bootstrap";
import { Zap, UserPlus, CheckCircle } from "lucide-react";
import { useWorkspaces } from "../../hooks/useWorkspaces";

const AcceptInvitePage = ({ auth }) => {
  const token = auth.user?.id_token;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { joinWorkspace } = useWorkspaces({ token });

  // State
  const [inviteToken, setInviteToken] = useState("");
  const [displayName, setDisplayName] = useState(auth?.user?.username || "");
  const [status, setStatus] = useState({ loading: false, error: null, success: false });

  // Automatically grab token from URL if present (e.g., /join?token=abc-123)
  // Also check localStorage in case the token was saved before an auth redirect
  useEffect(() => {
    const urlToken = searchParams.get("token");
    const savedToken = localStorage.getItem("pendingInviteToken");
    const effectiveToken = urlToken || savedToken;
    if (effectiveToken) {
      setInviteToken(effectiveToken);
      if (savedToken) localStorage.removeItem("pendingInviteToken");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inviteToken || !displayName) return;

    setStatus({ loading: true, error: null, success: false });

    try {
      await joinWorkspace(inviteToken, displayName);
      setStatus({ loading: false, error: null, success: true });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/workspaces"); // Adjust path to your main workspace manager
      }, 2000);
    } catch (err) {
      setStatus({
        loading: false,
        error: err.response?.data?.error?.message || "Invalid or expired invite token.",
        success: false
      });
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center vh-100 bg-light">
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          <div className="text-center mb-4">
            <div className="bg-primary d-inline-block p-3 rounded-circle mb-3 shadow-sm">
              <Zap size={32} className="text-white" />
            </div>
            <h2 className="fw-bold">Join Workspace</h2>
            <p className="text-muted">You've been invited to collaborate!</p>
          </div>

          {!auth.isAuthenticated ? (
            <Card className="border-0 shadow-lg p-5 text-center">
              <h5 className="fw-bold mb-3">Please Sign In</h5>
              <p className="text-muted mb-4">You need to be logged in to accept this invitation.</p>
              <Button variant="primary" onClick={() => {
                const urlToken = searchParams.get("token");
                if (urlToken) localStorage.setItem("pendingInviteToken", urlToken);
                auth.login();
              }}>Sign In</Button>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg p-4">
              {status.success ? (
                <div className="text-center py-4">
                  <CheckCircle size={64} className="text-success mb-3" />
                  <h4 className="fw-bold">Success!</h4>
                  <p className="text-muted">Joining the workspace now...</p>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">INVITE TOKEN</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter token (e.g. 0000-0000)"
                      value={inviteToken}
                      onChange={(e) => setInviteToken(e.target.value)}
                      required
                      className="py-2"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold">YOUR DISPLAY NAME</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="How should others see you?"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                      className="py-2"
                    />
                  </Form.Group>

                  {status.error && (
                    <Alert variant="danger" className="py-2 small">
                      {status.error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                    disabled={status.loading}
                  >
                    {status.loading ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <UserPlus size={18} className="me-2" />
                    )}
                    {status.loading ? "Joining..." : "Accept Invitation"}
                  </Button>
                </Form>
              )}
            </Card>
          )}

          <div className="text-center mt-4">
            <Button variant="link" className="text-decoration-none text-muted small" onClick={() => navigate(-1)}>
              Cancel and go back
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AcceptInvitePage;